import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { FiPlus, FiSearch, FiFilter, FiMoreVertical, FiEye, FiEdit2, FiTrash2, FiDatabase, FiAlertTriangle, FiFileText, FiGrid, FiList } from "react-icons/fi";
import { useNavigate, Link } from "react-router-dom";
import axios from "../../utils/api/axios";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

interface Asset {
    id: string;
    name: string;
    summary: string;
    category: string;
    category_other?: string;
    criticality: string;
    lifecycle_status: string;
    data_classification: string;
    risk_owner: string;
    technical_owner: string;
    status: string;
    is_active_in_run?: boolean | number;
    updated_at: string;
    targets_count: number;
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const CriticalityBadge: React.FC<{ level: string }> = ({ level }) => {
    const styles: Record<string, string> = {
        "Critical": "bg-red-50 text-red-600 border-red-100",
        "High": "bg-orange-50 text-orange-600 border-orange-200",
        "Medium": "bg-amber-50 text-amber-600 border-amber-200",
        "Low": "bg-blue-50 text-blue-600 border-blue-100",
        "Very Low": "bg-slate-50 text-slate-600 border-slate-100",
    };

    return (
        <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold border ${styles[level] || styles["Medium"]}`}>
            {level}
        </span>
    );
};

const StatusBadge: React.FC<{ 
    asset: Asset, 
    canEdit: boolean,
    onStatusChange: (assetId: string, newStatus: string) => Promise<void>
}> = ({ asset, canEdit, onStatusChange }) => {
    const isActive = asset.status?.toLowerCase() === "active";
    const statusOptions = ["Active", "Draft", "Deactivated"];
    
    // Normalize case for display
    const currentStatus = asset.status ? asset.status.charAt(0).toUpperCase() + asset.status.slice(1).toLowerCase() : "Active";
    
    // If they can edit AND it's not locked in a run, show a dropdown
    if (canEdit && !asset.is_active_in_run) {
        return (
            <select
                onClick={(e) => e.stopPropagation()}
                value={currentStatus}
                onChange={(e) => onStatusChange(asset.id, e.target.value)}
                className={`outline-none cursor-pointer appearance-none rounded-full px-3 py-1 text-[11px] font-bold transition-all border border-transparent hover:border-gray-200 focus:ring-2 focus:ring-blue-500/50 ${
                    isActive ? "bg-[#e2f9eb] text-[#2ba972]" : 
                    currentStatus === "Deactivated" ? "bg-red-50 text-red-600" : 
                    "bg-amber-50 text-amber-600"
                }`}
                style={{ textAlignLast: 'center' }}
            >
                {statusOptions.map(s => (
                    <option key={s} value={s} className="bg-white text-gray-800 text-left font-semibold">{s}</option>
                ))}
            </select>
        );
    }

    // Static badge for non-editors or locked assets
    return (
        <div 
            className="group/status relative inline-flex"
            title={asset.is_active_in_run ? "Status is locked while this asset is in an active or scheduled program run." : ""}
        >
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold ${
                isActive ? "bg-[#e2f9eb] text-[#2ba972]" : 
                currentStatus === "Deactivated" ? "bg-red-50 text-red-600" : 
                "bg-amber-50 text-amber-600"
            } ${asset.is_active_in_run && canEdit ? "opacity-75 ring-1 ring-gray-300" : ""}`}>
                {currentStatus}
                {asset.is_active_in_run && canEdit && <span className="ml-1.5 text-gray-400">🔒</span>}
            </span>
            {asset.is_active_in_run && canEdit && (
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover/status:block w-48 p-2 bg-gray-900 text-white text-[10px] rounded shadow-xl z-[100] text-center leading-tight">
                    Locked during active runs
                </div>
            )}
        </div>
    );
};

interface AssetInventoryPageProps {
    companyId?: number;
}

const getPaginationRange = (currentPage: number, totalPages: number): (number | string)[] => {
    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const range: (number | string)[] = [];
    const showLeftEllipsis = currentPage > 3;
    const showRightEllipsis = currentPage < totalPages - 2;

    if (!showLeftEllipsis && showRightEllipsis) {
        for (let i = 1; i <= 4; i++) range.push(i);
        range.push('...');
        range.push(totalPages);
    } else if (showLeftEllipsis && !showRightEllipsis) {
        range.push(1);
        range.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) range.push(i);
    } else {
        range.push(1);
        range.push('...');
        range.push(currentPage - 1);
        range.push(currentPage);
        range.push(currentPage + 1);
        range.push('...');
        range.push(totalPages);
    }
    return range;
};

const AssetInventoryPage: React.FC<AssetInventoryPageProps> = ({ companyId }) => {
    usePageTitle("Asset Inventory");
    const navigate = useNavigate();
    const { role: userRole, username } = useUserCredentials();
    const canCreate = userRole === UserRole.ADMIN ||
        userRole === UserRole.SUB_ADMIN ||
        userRole === UserRole.BUSINESS ||
        userRole === UserRole.BUSINESS_ADMINISTRATOR;

    const isAdminRole = userRole === UserRole.ADMIN || userRole === UserRole.SUB_ADMIN || userRole === UserRole.ADMIN_MANAGER;

    const canEditAsset = (asset: Asset) => {
        if (canCreate) return true;
        return asset.risk_owner === username || asset.technical_owner === username;
    };

    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterCategory, setFilterCategory] = useState("All");
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
    const [currentPage, setCurrentPage] = useState(1);
    const [menuAssetId, setMenuAssetId] = useState<string | null>(null);

    const itemsPerPage = viewMode === "grid" ? 9 : 10;

    const fetchAssets = async () => {
        setLoading(true);
        try {
            const config = companyId ? { params: { company_id: companyId } } : {};
            const response = await axios.get("/assets", config);
            setAssets(response.data);
        } catch (err: any) {
            console.error("Error fetching assets:", err);
            toast.error("Failed to load assets. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string, name: string) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await axios.delete(`/assets/${id}`);
            toast.success("Asset deleted successfully");
            fetchAssets();
        } catch (err: any) {
            console.error("Error deleting asset:", err);
            toast.error(err.response?.data?.message || "Failed to delete asset");
        }
        setMenuAssetId(null);
    };

    const handleStatusChange = async (assetId: string, newStatus: string) => {
        try {
            // Optimistically update the UI
            setAssets(prev => prev.map(a => a.id === assetId ? { ...a, status: newStatus } : a));
            
            const promise = axios.put(`/assets/${assetId}/status`, { status: newStatus });
            
            toast.promise(promise, {
                loading: "Updating status...",
                success: `Asset is now ${newStatus}`,
                error: (err) => {
                    // Revert UI on failure
                    fetchAssets();
                    return err.response?.data?.message || "Failed to update asset status";
                }
            });
            
            await promise;
        } catch (error) {
            console.error("Status update error", error);
        }
    };

    useEffect(() => {
        fetchAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredAssets = useMemo(() => {
        return assets.filter(asset => {
            const matchesSearch =
                asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                asset.risk_owner.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesCategory = filterCategory === "All" || asset.category === filterCategory;

            return matchesSearch && matchesCategory;
        });
    }, [assets, searchQuery, filterCategory]);

    const categories = useMemo(() => {
        const cats = new Set(assets.map(a => a.category).filter(Boolean));
        return ["All", ...Array.from(cats)].sort();
    }, [assets]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterCategory, viewMode]);

    const paginatedAssets = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredAssets.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAssets, currentPage, itemsPerPage]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    if (loading && assets.length === 0) {
        return (
            <div className="min-h-screen bg-slate-50 p-8 flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-4 text-slate-600 font-medium">Loading your assets...</p>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            className="min-h-screen bg-gray-50 p-6 md:p-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Asset Inventory</h1>
                    <p className="text-gray-500 text-sm mt-1">Manage and monitor all your registered digital assets.</p>
                </div>
            </div>

            {/* Hero Stats with Colored Top Borders */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border-t-4 border-blue-500">
                    <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total Assets</span>
                            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                <FiDatabase className="w-5 h-5 text-blue-500" />
                            </div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{assets.length}</p>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border-t-4 border-red-500">
                    <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Critical Assets</span>
                            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                                <FiAlertTriangle className="w-5 h-5 text-red-500" />
                            </div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{assets.filter(a => a.criticality === "Critical").length}</p>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border-t-4 border-yellow-500">
                    <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Draft Status</span>
                            <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                                <FiFileText className="w-5 h-5 text-yellow-500" />
                            </div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{assets.filter(a => a.status === "draft").length}</p>
                    </div>
                </div>
            </div>

            {/* Dark Navy Search & Filter Bar */}
            <div className="bg-[#1a1f37] rounded-lg p-4 mb-6 flex flex-col md:flex-row gap-4 items-center shadow-lg">
                <div className="relative flex-1 w-full">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search by asset name or owner..."
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white border-0 focus:ring-2 focus:ring-green-500 transition-all outline-none text-sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* View Toggle */}
                <div className="bg-[#0f111a] p-1 rounded-lg flex items-center border border-slate-700">
                    <button
                        onClick={() => setViewMode("grid")}
                        className={`p-2 rounded-md transition-all ${viewMode === "grid" ? "bg-slate-700 text-white shadow-sm" : "text-slate-400 hover:text-slate-200"}`}
                        title="Grid View"
                    >
                        <FiGrid className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setViewMode("list")}
                        className={`p-2 rounded-md transition-all ${viewMode === "list" ? "bg-slate-700 text-white shadow-sm" : "text-slate-400 hover:text-slate-200"}`}
                        title="List View"
                    >
                        <FiList className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                    <select
                        className="bg-white border-0 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-500 w-full md:w-48"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                    >
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                    {/* Only show Register button for creator roles (not in company view unless admin) */}
                    {(!companyId || isAdminRole) && canCreate && (
                        <button
                            onClick={() => {
                                if (companyId) {
                                    sessionStorage.setItem("ctb_admin_active_company_id", String(companyId));
                                    navigate(`/dashboard/companies/${companyId}/assets/create`);
                                } else {
                                    navigate("/dashboard/assets/create");
                                }
                            }}
                            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-lg font-semibold transition-all shadow-md whitespace-nowrap"
                        >
                            <FiPlus className="w-5 h-5" />
                            Register New Asset
                        </button>
                    )}
                </div>
            </div>

            {/* Asset Content */}
            {viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">

                    {paginatedAssets.length > 0 ? (
                        paginatedAssets.map((asset) => (
                            <motion.div
                                key={asset.id}
                                variants={itemVariants}
                                onClick={() => navigate(companyId ? `/dashboard/companies/${companyId}/assets/${asset.id}` : `/dashboard/assets/${asset.id}`)}
                                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow flex flex-col h-full cursor-pointer group"
                            >
                                {/* Card Header */}
                                <div className="p-5 flex-1">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <h3 className="font-bold text-[#0f172a] text-[18px] mb-1 tracking-tight">{asset.name}</h3>
                                            <p className="text-[13px] text-[#64748b] font-medium leading-relaxed line-clamp-1">{asset.summary}</p>
                                        </div>
                                        <div className="relative">
                                            {canEditAsset(asset) && (
                                                <>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMenuAssetId(menuAssetId === asset.id ? null : asset.id);
                                                        }}
                                                        className={`p-1.5 rounded-full transition-colors ${menuAssetId === asset.id ? "bg-slate-100 text-slate-700" : "text-gray-400 hover:text-gray-600 hover:bg-slate-50"}`}
                                                    >
                                                        <FiMoreVertical className="w-4 h-4" />
                                                    </button>

                                                    <AnimatePresence>
                                                        {menuAssetId === asset.id && (
                                                            <>
                                                                {/* Backdrop for click-away */}
                                                                <div
                                                                    className="fixed inset-0 z-10"
                                                                    onClick={() => setMenuAssetId(null)}
                                                                />
                                                                <motion.div
                                                                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                                                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                                                    className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-xl border border-slate-100 py-1.5 z-20 origin-top-right overflow-hidden"
                                                                >
                                                                    <Link
                                                                        to={`/dashboard/assets/create/${asset.id}`}
                                                                        className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                                                                    >
                                                                        <FiEdit2 className="w-3.5 h-3.5" />
                                                                        Edit Details
                                                                    </Link>
                                                                    <Link
                                                                        to={`/dashboard/assets/${asset.id}`}
                                                                        className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                                                                    >
                                                                        <FiEye className="w-3.5 h-3.5" />
                                                                        View Asset
                                                                    </Link>
                                                                    <hr className="my-1 border-slate-50" />
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDelete(asset.id, asset.name);
                                                                        }}
                                                                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors"
                                                                    >
                                                                        <FiTrash2 className="w-3.5 h-3.5" />
                                                                        Delete Asset
                                                                    </button>
                                                                </motion.div>
                                                            </>
                                                        )}
                                                    </AnimatePresence>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Info Grid - 2x2 Layout */}
                                    <div className="grid grid-cols-2 gap-y-8 mt-8 mb-4">
                                        {/* Category */}
                                        <div className="flex flex-col gap-1.5">
                                            <span className="text-[#94a3b8] font-semibold text-[13px] tracking-wide">Category</span>
                                            <span className="text-[#334155] font-bold text-[14px] tracking-tight">
                                                {asset.category === "Other" ? asset.category_other : asset.category}
                                            </span>
                                        </div>

                                        {/* Criticality */}
                                        <div className="flex flex-col gap-2 items-end">
                                            <span className="text-[#94a3b8] font-semibold text-[13px] tracking-wide">Criticality</span>
                                            <CriticalityBadge level={asset.criticality} />
                                        </div>

                                        {/* Lifecycle */}
                                        <div className="flex flex-col gap-1.5">
                                            <span className="text-[#94a3b8] font-semibold text-[13px] tracking-wide">Lifecycle</span>
                                            <span className="text-[#334155] font-bold text-[14px] tracking-tight">{asset.lifecycle_status}</span>
                                        </div>

                                        {/* Status */}
                                        <div className="flex flex-col gap-2 items-end">
                                            <span className="text-[#94a3b8] font-semibold text-[13px] tracking-wide">Status</span>
                                            <StatusBadge asset={asset} canEdit={canEditAsset(asset)} onStatusChange={handleStatusChange} />
                                        </div>
                                    </div>
                                </div>

                                {/* Card Footer - Premium Dark Navy */}
                                <div className="bg-[#030816] px-5 py-3.5 flex items-center justify-between border-t border-slate-800">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-[#112323] rounded-full flex items-center justify-center text-[#2ba972] border border-[#1a3a3a]">
                                            <span className="font-bold text-xs">{asset.risk_owner.charAt(0).toLowerCase()}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <p className="text-[12px] text-slate-100 font-semibold">{asset.risk_owner.toLowerCase()}</p>
                                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight">RISK OWNER</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-5">
                                        {asset.targets_count === 0 && canEditAsset(asset) ? (
                                            <Link
                                                to={companyId ? `/dashboard/companies/${companyId}/assets/${asset.id}?add-target=true` : `/dashboard/assets/${asset.id}?add-target=true`}
                                                className="bg-[#2ba972] text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-[#38ca8a] transition-all flex items-center gap-1.5"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <FiPlus className="w-3.5 h-3.5" />
                                                Add Target
                                            </Link>
                                        ) : (
                                            <Link
                                                to={companyId ? `/dashboard/companies/${companyId}/assets/${asset.id}` : `/dashboard/assets/${asset.id}`}
                                                className="text-[#2ba972] hover:text-[#38ca8a] text-sm font-bold"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                View
                                            </Link>
                                        )}
                                        {canEditAsset(asset) && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/dashboard/assets/create/${asset.id}`);
                                                }}
                                                className="text-slate-300 hover:text-white"
                                            >
                                                <FiEdit2 className="w-4.5 h-4.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))
                    ) : (
                        <div className="col-span-full">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                                <div className="flex flex-col items-center">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                        <FiSearch className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-gray-900 font-semibold text-lg">No assets found</h3>
                                    <p className="text-gray-500 mt-1 text-sm">Try adjusting your filters or register a new asset.</p>
                                    <button
                                        onClick={() => { setSearchQuery(""); setFilterCategory("All"); }}
                                        className="mt-4 text-green-600 font-semibold hover:underline text-sm"
                                    >
                                        Clear all filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Asset Name</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Category</th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">Criticality</th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Risk Owner</th>
                                    <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {paginatedAssets.length > 0 ? (
                                    paginatedAssets.map((asset) => (
                                        <tr 
                                            key={asset.id} 
                                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                                            onClick={() => navigate(companyId ? `/dashboard/companies/${companyId}/assets/${asset.id}` : `/dashboard/assets/${asset.id}`)}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-slate-900 text-sm mb-0.5">{asset.name}</span>
                                                    <span className="text-xs text-slate-500 line-clamp-1 max-w-[200px]">{asset.summary}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-medium text-slate-600">
                                                    {asset.category === "Other" ? asset.category_other : asset.category}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <CriticalityBadge level={asset.criticality} />
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <StatusBadge asset={asset} canEdit={canEditAsset(asset)} onStatusChange={handleStatusChange} />
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 border border-slate-200">
                                                        {asset.risk_owner.charAt(0).toLowerCase()}
                                                    </div>
                                                    <span className="text-sm font-medium text-slate-600">{asset.risk_owner}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-3">
                                                    {canEditAsset(asset) && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/dashboard/assets/create/${asset.id}`);
                                                            }}
                                                            className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 transition-colors"
                                                        >
                                                            <FiEdit2 className="w-4 h-4" />
                                                            <span className="text-xs font-semibold">Edit</span>
                                                        </button>
                                                    )}
                                                    <Link
                                                        to={companyId ? `/dashboard/companies/${companyId}/assets/${asset.id}${asset.targets_count === 0 && canEditAsset(asset) ? '?add-target=true' : ''}` : `/dashboard/assets/${asset.id}${asset.targets_count === 0 && canEditAsset(asset) ? '?add-target=true' : ''}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={`flex items-center gap-1.5 transition-colors font-medium ${(asset.targets_count === 0 && canEditAsset(asset)) ? 'text-green-600 hover:text-green-700' : 'text-blue-600 hover:text-blue-700'}`}
                                                    >
                                                        <span className="text-xs font-bold">{(asset.targets_count === 0 && canEditAsset(asset)) ? 'Add Target' : 'View'}</span>
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                                            <div className="flex flex-col items-center">
                                                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                                                    <FiSearch className="w-5 h-5 text-slate-400" />
                                                </div>
                                                <p className="font-medium">No assets found matching your criteria</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}


            {/* Pagination Footer */}
            {totalPages > 1 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex items-center justify-between mt-6">
                    <p className="text-sm text-gray-600">
                        Showing <span className="font-semibold">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-semibold">{Math.min(currentPage * itemsPerPage, filteredAssets.length)}</span> of <span className="font-semibold">{filteredAssets.length}</span> assets
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${currentPage === 1
                                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                                }`}
                        >
                            Previous
                        </button>
                        <div className="flex items-center gap-1">
                            {getPaginationRange(currentPage, totalPages).map((item, index) => {
                                if (item === "...") {
                                    return <span key={`ellipsis-${index}`} className="text-slate-400 px-1 font-semibold">...</span>;
                                }
                                const page = item as number;
                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`w-8 h-8 rounded-lg text-sm font-semibold transition-all ${currentPage === page
                                            ? "bg-[#1a1f37] text-white"
                                            : "text-slate-600 hover:bg-slate-100"
                                            }`}
                                    >
                                        {page}
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${currentPage === totalPages
                                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                : "bg-[#1a1f37] text-white hover:bg-[#252b47]"
                                }`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default AssetInventoryPage;
